import React, { FC, useRef, useState } from 'react';
import { AppBar, Box, Container, Fade, Grid, makeStyles, Theme, Toolbar, Typography } from '@material-ui/core';
import Link from 'next/link';
import { routes } from 'config/routes/routes';
import { MenuProps } from 'containers/layout/navbar/types';
import { LogoU1 } from 'svgs';
import { TLabelLinks } from 'containers/layout/types';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import AccentLink from 'components/atoms/AccentLink';

interface NavLinkProps {
  path: string;
  label: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
  },
  toolbar: {
    padding: theme.spacing(5, 0),
    transition: 'padding 300ms ease-out',
    maxHeight: 81,
  },
  link: {
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(6),
    fontWeight: 500,
    '&:hover': {
      color: theme.palette.primary.main,
      cursor: 'pointer',
    },
  },
  linkList: {
    margin: theme.spacing(4, 0, 2, 4),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4),
    minWidth: 210,
    border: `1px solid #C4C4C4`,
    position: 'absolute',
    top: '90%',
    left: '50%',
    transform: 'translate(-50%, 1.5%)',
    height: 'auto',
  },
  linkListServices: {
    margin: theme.spacing(6, -250, 2, -80),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(5, 5, 2),
    maxWidth: 1130,
    position: 'absolute',
    maxHeight: 124,
    display: 'flex',
    border: `1px solid ${theme.palette.primary.main}`,
    [theme.breakpoints.up('xl')]: {
      maxWidth: 1350,
      margin: theme.spacing(4, -70, 2),
    },
  },
  label: {
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  dropdownLink: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 300ms',
    paddingLeft: theme.spacing(6),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  text: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(6),
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  container: {
    maxWidth: 1500,
  },
}));

const DesktopMenu: FC<MenuProps> = () => {
  const classes = useStyles();

  const NavLink: FC<NavLinkProps> = ({ label, path }) => {
    const ref = useRef(null);

    return (
      <div ref={ref}>
        <Link href={path}>
          <Typography className={classes.link}>{label}</Typography>
        </Link>
      </div>
    );
  };

  return (
    <AppBar position="fixed" className={classes.root}>
      <Toolbar className={classes.toolbar}>
        <Container className={classes.container}>
          <Grid container>
            <Grid xl={2} lg={1} item>
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Link href={'/'}>
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <LogoU1 />
                  </Box>
                </Link>
              </Box>
            </Grid>
            <Grid item xl={10} lg={11}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                {routes.map(({ label, links, route }, index) => (
                  <Box key={index}>
                    {links ? (
                      <NavLinkDropdown label={label} links={links} route={route} classes={classes} />
                    ) : route === '/contact-us' ? (
                      <AccentLink href={route}>{label}</AccentLink>
                    ) : (
                      <NavLink label={label} path={route} />
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Toolbar>
    </AppBar>
  );
};

interface INavLinkDropdown {
  label: string;
  links: TLabelLinks[];
  route: string;
  classes: ClassNameMap<string>;
}

const NavLinkDropdown: FC<INavLinkDropdown> = ({ classes, route, label, links }) => {
  const [showMore, setShowMore] = useState<boolean>(false);

  const toggleShowMore = () => setShowMore(true);
  const toggleShowLess = () => setShowMore(false);

  return (
    <Box onMouseEnter={toggleShowMore} onMouseLeave={toggleShowLess} position="relative">
      <Link href={route} passHref>
        <Typography variant="body1" component="span" className={classes.text}>
          {label}
        </Typography>
      </Link>
      <Fade in={showMore}>
        <Box>
          {links.map(({ label, links }, index) => (
            <Box key={index} className={classes.linkList}>
              {links.map(({ label, route, isLastLink }, indexLink) => {
                return (
                  <Box mb={1} key={route + label}>
                    {!isLastLink && (
                      <Link href={route}>
                        <Typography component="span" color={'textSecondary'} className={classes.label}>
                          {label}
                        </Typography>
                      </Link>
                    )}
                    {isLastLink && (
                      <>
                        <Box borderBottom="1px solid rgba(45, 47, 67, 0.5)" maxWidth={176} mt={1.5} mb={1.5} />
                        <Link href={route}>
                          <Typography component="span" color={'textSecondary'} className={classes.label}>
                            {label}
                          </Typography>
                        </Link>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Fade>
    </Box>
  );
};

const NavLinkDropdownServices: FC<INavLinkDropdown> = ({ classes, route, label, links }) => {
  const [showMore, setShowMore] = useState<boolean>(false);

  const toggleShowMore = () => setShowMore(true);
  const toggleShowLess = () => setShowMore(false);

  return (
    <Box onMouseEnter={toggleShowMore} onMouseLeave={toggleShowLess} position="relative">
      <Link href={route} passHref>
        <Typography variant="body1" component="span" className={classes.text}>
          {label}
        </Typography>
      </Link>
      <Fade in={showMore}>
        <Box>
          {links.map(({ label, links }, index) => (
            <Box key={index} className={classes.linkListServices}>
              <Box display="flex" flexWrap="wrap" justifyContent="flex-start" alignItems="center">
                {links.map(({ label, route }) => (
                  <Box key={route + label} width="20%" mb={2}>
                    <Link href={route}>
                      <Typography component="span" color="textSecondary" className={classes.label}>
                        {label}
                      </Typography>
                    </Link>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Fade>
    </Box>
  );
};

export default DesktopMenu;
